import type { Router } from 'vue-router';
import { IsSafeRelativePath } from './LoginRedirect';

export interface RoutableNotification {
  type: string;
  payload?: {
    accountId?: string | number;
    accountName?: string;
    programId?: string | number;
    queueItemId?: number;
    documentId?: number;
    isPolicyBinder?: boolean;
    submissionId?: string | number;
    path?: string;
  };
}

// Mirrored by documentExceptionURL in the API's email template, so the email and the
// in-app notification open the same row. Binder Scans only lists binders on a live policy
// of the account; anything else, and payloads written before isPolicyBinder existed, opens
// the admin Extraction Runs failures instead.
function documentExceptionPath(payload: RoutableNotification['payload']): string {
  const { accountId, accountName, documentId, isPolicyBinder, queueItemId } = payload ?? {};
  if (isPolicyBinder && accountId && documentId) {
    return `/account/${accountId}/policy-detail/binder-reviews?status=failed&documentId=${documentId}`;
  }

  const query = new URLSearchParams({ tab: 'results', status: 'Failed' });
  if (accountId && accountName) query.set('accountSearch', accountName);
  if (queueItemId) query.set('failureId', String(queueItemId));
  return `/admin/ai?${query}`;
}

export interface NotificationNavigationOptions {
  forceRouteRefresh?: boolean;
}

export async function routeFromNotification(
  router: Router,
  notification: RoutableNotification,
  options: NotificationNavigationOptions = {},
): Promise<void> {
  const accountId = notification.payload?.accountId;
  const programId = notification.payload?.programId;
  const submissionId = notification.payload?.submissionId;
  const forceRouteRefresh = options.forceRouteRefresh ?? false;

  const navigate = async (path: string): Promise<void> => {
    if (forceRouteRefresh) {
      // if user is already at the target route and click a notification to that route, vue-router
      // suppresses duplicate navigation; bouncing through '/' guarantees route actually changes
      await router.push('/');
      await router.replace(path);
    } else {
      await router.push(path);
    }
  };

  switch (notification.type) {
    case 'new-policies-added':
      if (!accountId) return;
      await navigate(`/account/${accountId}/policy-detail`);
      return;
    case 'policies-up-for-renewal':
      if (!accountId || !programId) return;
      await navigate(`/account/${accountId}/programs/${programId}`);
      return;
    case 'new-document-upload':
    case 'binder-ready-for-validation':
      if (!accountId) return;
      await navigate(`/account/${accountId}/documents`);
      return;
    case 'external-policy-ready-for-review':
      if (!accountId || !submissionId) return;
      await navigate(`/account/${accountId}/policy-detail/external-policies/review/${submissionId}`);
      return;
    case 'document-exception':
      await navigate(documentExceptionPath(notification.payload));
      return;
    case 'dashboard-subscription': {
      const path = notification.payload?.path;
      if (typeof path !== 'string' || !IsSafeRelativePath(path)) return;
      await navigate(path);
      return;
    }
    default:
      return;
  }
}
